/* eslint-disable react-hooks/exhaustive-deps */
/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-06-21 11:39:10
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withI18n } from '@shopify/react-i18n';

import General from './general';
import VisualEditor from './visual-editor';
import Placement from './placement';

import {
  enterSABFullscreen,
  exitSABFullscreen
} from 'src/integrations/shopify-app-bridge/fullscreen';
import { enableShopifyIntegration } from 'src/helpers/theme-setup-helper';
import history from 'src/routes/history';
import { routes } from 'src/routes';
import { handleOpenFullscreen } from 'actions/app-integration-actions';
import { loadCSS, removeScript } from 'src/helpers';

import * as filterDataActions from 'actions/filter-data-actions';
import * as themeSetupV3Actions from 'actions/theme-setup-v3-actions';
import * as recommendationAction from 'src/stores/actions/recommendation-actions';
import * as themeSettingActions from 'src/stores/actions/theme-settings-actions';
import * as themeIntegrationAction from 'actions/shopify-integration/theme-integration-actions';
import * as templateInstanceAction from 'actions/shopify-integration/template-instance-actions';
import * as bundleActions from 'src/stores/actions/bundle-actions';

import type { ComposedProps, State } from './new.type';
const host = process.env.REACT_APP_BOOST_CDN_URL;

const WidgetItem = (props: ComposedProps): Component<ComposedProps, State> => {
  let unsubscribeExit = undefined;
  let mainCSS = null;
  const {
    success,
    bundleWidget,
    getWidget,
    getDefaultDesignSettings,
    getAllThemes,
    getAllThemesLoading,
    allThemes,
    checkEnableBoostCore,
    handleOpenFullscreen,
    isEnableShopifyIntegration,
    resetPredictiveBundle,
    checkThemeIntegrationStatus,
    getThemeSettingById,
    getBoostTemplateInstances,
    getBoostTemplatesLoading,
    boostTemplates
  } = props;
  const { widgetId } = props.match.params;

  const [step, setStep] = useState(0);
  const [dirtyOnStep, setDirtyOnStep] = useState({ step_1: false, step_2: false });
  const [widget, setWidget] = useState({});

  // Handle back to management page
  const backToManagementPage = () => {
    history.push(routes.predictiveBundle.list());
  };

  // Handle action opening the fullscreen
  const handleOpenFullscreenPage = () => {
    unsubscribeExit = enterSABFullscreen(() => {
      backToManagementPage();
      handleOpenFullscreen(false);
    });
    handleOpenFullscreen(true);
  };

  // Handle action closing the fullscreen
  const handleCloseFullscreenPage = (runExitFullscreen = true) => {
    backToManagementPage();
    if (runExitFullscreen) {
      exitSABFullscreen();
      handleOpenFullscreen(false);
    }
  };

  const processTheme = allThemes => {
    const mainTheme = _.find(allThemes, theme => theme.role === 'main') || {};
    getThemeSettingById(mainTheme.id);
    if (isEnableShopifyIntegration) {
      checkThemeIntegrationStatus(mainTheme.id);
    }
  };

  useEffect(() => {
    handleOpenFullscreenPage();

    if (!!widgetId) {
      getWidget(widgetId);
    } else {
      getDefaultDesignSettings();
    }

    if (_.isEmpty(allThemes)) {
      getAllThemes();
    }

    // Get enable boost core on live theme
    checkEnableBoostCore({});

    getBoostTemplateInstances();
    return () => {
      unsubscribeExit();
      resetPredictiveBundle();
      removeScript(mainCSS);
    };
  }, []);

  useEffect(() => {
    if (!_.isEmpty(allThemes)) {
      processTheme(allThemes);
    }
  }, [getAllThemesLoading]);

  useEffect(() => {
    if (!_.isEmpty(bundleWidget)) {
      setWidget(bundleWidget);
    }
  }, [success, bundleWidget]);

  useEffect(() => {
    if (_.isEmpty(mainCSS) && !_.isEmpty(boostTemplates)) {
      const boostTemplate = _.find(boostTemplates, { themeNameLib: 'default' });
      const boostTemplateVersion =
        process.env.REACT_APP_ENVIRONMENT === 'production'
          ? _.get(boostTemplate, 'templateVersion', '1.0.0')
          : 'staging';

      mainCSS = loadCSS(
        `${host}/widget-integration/theme/default/${boostTemplateVersion}/main.css`
      );
    }
  }, [getBoostTemplatesLoading]);

  let content = <div />;
  switch (step) {
    case 0: {
      // render General
      content = (
        <General
          widget={widget}
          setWidget={setWidget}
          handleCloseFullscreenPage={handleCloseFullscreenPage}
          currentStep={step}
          setStep={setStep}
          dirtyOnStep={dirtyOnStep}
          setDirtyOnStep={setDirtyOnStep}
        />
      );
      break;
    }
    case 1: {
      // render design
      content = (
        <VisualEditor
          isCreateFlow={_.isEmpty(widgetId)}
          widget={widget}
          setWidget={setWidget}
          handleCloseFullscreenPage={handleCloseFullscreenPage}
          currentStep={step}
          setStep={setStep}
          dirtyOnStep={dirtyOnStep}
          setDirtyOnStep={setDirtyOnStep}
        />
      );
      break;
    }
    case 2: {
      // render placement
      content = <Placement handleCloseFullscreenPage={handleCloseFullscreenPage} />;
      break;
    }
    default:
  }

  return <div className="BC-Page PFS-PredictiveBundleItem">{content}</div>;
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  // Get recommendation widgets
  getWidget: (widgetId: string) => dispatch(bundleActions.getWidget(widgetId, ownProps.i18n)),
  resetPredictiveBundle: () => dispatch(bundleActions.resetPredictiveBundle()),
  handleOpenFullscreen: (isOpenFullScreen: boolean) =>
    dispatch(handleOpenFullscreen(isOpenFullScreen)),
  getFilterData: () => dispatch(filterDataActions.getFilterData(ownProps.i18n, true)),
  getAllThemes: () => dispatch(themeSetupV3Actions.getAllThemes(ownProps.i18n)),
  getThemeSettingById: id =>
    dispatch(themeSettingActions.getThemeSettingById(id, ownProps.i18n, 'recommendation')),
  checkThemeIntegrationStatus: themeId =>
    dispatch(themeIntegrationAction.checkThemeIntegrationStatus(ownProps.i18n, themeId)),
  getBoostTemplateInstances: () =>
    dispatch(templateInstanceAction.getBoostTemplateInstances(ownProps.i18n)),
  // get default design settings for widget
  getDefaultDesignSettings: () => dispatch(bundleActions.getDefaultDesignSettings(ownProps.i18n)),
  // Check enable boost core on live theme
  checkEnableBoostCore: queries =>
    dispatch(recommendationAction.checkEnableBoostCore(queries, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  const { templateInstance = {} } = state;
  return {
    invoice: _.get(state.subscriptionV3, 'invoice', {}),
    loading: _.get(state.bundle, 'loading', false),
    success: _.get(state.bundle, 'success', false),
    saving: _.get(state.bundle, 'saving', false),
    saveSuccess: _.get(state.bundle, 'saveSuccess', false),
    // widgetId: _.get(state.bundle, 'widgetId', ''),
    bundleWidget: _.get(state.bundle, 'widget', {}),
    accessScopes: _.get(state.oauth, 'tenantApp.accessScopes', {}),
    allThemes: _.get(state.themeSetupV3, 'allThemes', []),
    getAllThemesLoading: _.get(state.themeSetupV3, 'getAllThemesLoading', false),
    defaultDesignSettingsLoading: _.get(state.bundle, 'defaultDesignSettingsLoading', false),
    defaultDesignSettingsLoadedSuccess: _.get(
      state.bundle,
      'defaultDesignSettingsLoadedSuccess',
      false
    ),
    defaultDesignSettings: _.get(state.bundle, 'defaultDesignSettings', {}),
    isEnableShopifyIntegration: enableShopifyIntegration(state.oauth.tenantApp),
    getBoostTemplatesLoading: _.get(templateInstance, 'getBoostTemplatesLoading', false),
    boostTemplates: _.get(templateInstance, 'boostTemplates', [])
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(WidgetItem));
